import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../supabase'
import './ConectarWhatsapp.css'

export default function ConectarWhatsapp({ comercioId }) {
  const [instancia, setInstancia] = useState(null)
  const [status, setStatus] = useState('DESCONECTADO')
  const [qrCode, setQrCode] = useState('')
  const [loading, setLoading] = useState(true)
  
  // Estados para UI moderna (Errores y confirmación)
  const [errorLocal, setErrorLocal] = useState('')
  const [modalVisible, setModalVisible] = useState(false)

  // 🚀 Usamos las variables de entorno centralizadas y seguras
  const EVOLUTION_URL = process.env.REACT_APP_EVOLUTION_URL
  const API_KEY = process.env.REACT_APP_EVOLUTION_API_KEY
  const WEBHOOK_URL = `${process.env.REACT_APP_API_URL}/webhook`

  const asignarWebhook = useCallback(async (nombreInstancia) => {
    try {
      await fetch(`${EVOLUTION_URL}/webhook/set/${nombreInstancia}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'apikey': API_KEY },
        body: JSON.stringify({
          webhook: {
            enabled: true,
            url: WEBHOOK_URL,
            webhookByEvents: false,
            events: ["MESSAGES_UPSERT"]
          }
        })
      })
    } catch (err) {
      console.error('Error al configurar el webhook:', err)
    }
  }, [EVOLUTION_URL, API_KEY, WEBHOOK_URL])

  const verificarEstado = useCallback(async (nombreInstancia) => {
    try {
      const res = await fetch(`${EVOLUTION_URL}/instance/connectionState/${nombreInstancia}`, {
        headers: { 'apikey': API_KEY }
      })
      if (!res.ok) return 'DESCONECTADO'
      const data = await res.json()
      
      if (data.instance?.state === 'open') {
        await asignarWebhook(nombreInstancia)
        return 'CONECTADO'
      }
      return 'DESCONECTADO'
    } catch (err) {
      console.error("Error verificando estado:", err)
      return 'DESCONECTADO'
    }
  }, [EVOLUTION_URL, API_KEY, asignarWebhook])

  useEffect(() => {
    async function inicializar() {
      setLoading(true)
      setErrorLocal('')
      
      const { data, error } = await supabase
        .from('comercios')
        .select('evolution_instance')
        .eq('id', comercioId)
        .single()

      if (!error && data?.evolution_instance) {
        setInstancia(data.evolution_instance)
        const estadoReal = await verificarEstado(data.evolution_instance)
        setStatus(estadoReal)
      }
      
      setLoading(false)
    }
    
    if (comercioId) inicializar()
  }, [comercioId, verificarEstado])

  const conectarDispositivo = async () => {
    if (!instancia) return
    setStatus('GENERANDO')
    setErrorLocal('')

    try {
      await fetch(`${EVOLUTION_URL}/instance/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'apikey': API_KEY },
        body: JSON.stringify({
          instanceName: instancia,
          integration: "WHATSAPP-BAILEYS",
          qrcode: true
        })
      })

      const res = await fetch(`${EVOLUTION_URL}/instance/connect/${instancia}`, {
        headers: { 'apikey': API_KEY }
      })
      const data = await res.json()

      if (data.base64) {
        setQrCode(data.base64) 
        setStatus('MOSTRANDO_QR')

        const intervalo = setInterval(async () => {
          const checkRes = await fetch(`${EVOLUTION_URL}/instance/connectionState/${instancia}`, {
            headers: { 'apikey': API_KEY }
          })
          if (checkRes.ok) {
            const checkData = await checkRes.json()
            if (checkData.instance?.state === 'open') {
              await asignarWebhook(instancia)
              setStatus('CONECTADO')
              setQrCode('')
              clearInterval(intervalo)
            }
          }
        }, 3000)
      } else {
        setErrorLocal("El servidor no pudo inicializar el código QR. Por favor, reintentá.")
        setStatus('DESCONECTADO')
      }
    } catch (err) {
      setErrorLocal('Hubo un problema de conexión con el servidor de WhatsApp.')
      setStatus('DESCONECTADO')
    }
  }

  const ejecutarDesconexion = async () => {
    setModalVisible(false)
    setLoading(true)
    setErrorLocal('')
    try {
      await fetch(`${EVOLUTION_URL}/instance/logout/${instancia}`, {
        method: 'DELETE',
        headers: { 'apikey': API_KEY }
      })
      setStatus('DESCONECTADO')
    } catch (error) {
      console.error("Error al desconectar:", error)
      setErrorLocal("No se pudo desvincular el celular de forma remota.")
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="wpp-loading">
        <svg className="wpp-spinner" viewBox="0 0 50 50">
          <circle cx="25" cy="25" r="20" fill="none" strokeWidth="4"></circle>
        </svg>
        <p>Verificando estado del canal de comunicación...</p>
      </div>
    )
  }

  return (
    <div className="wpp-container" style={{ maxWidth: '640px', margin: '0 auto', padding: '1.5rem 1rem' }}>
      
      {/* Banner de errores integrado */}
      {errorLocal && (
        <div style={{ backgroundColor: '#fef2f2', border: '1px solid #fca5a5', padding: '12px 16px', borderRadius: '10px', color: '#b91c1c', fontSize: '14px', marginBottom: '1.25rem', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '8px' }}>
          ⚠️ {errorLocal}
        </div>
      )}

      <div className="wpp-card" style={{ boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05), 0 2px 4px -1px rgba(0,0,0,0.03)', borderRadius: '16px', border: '1px solid #e2e8f0', background: '#fff', overflow: 'hidden' }}>
        
        {/* Encabezado limpio */}
        <div className="wpp-header" style={{ padding: '1.5rem 1.5rem 1rem 1.5rem', borderBottom: '1px solid #f1f5f9' }}>
          <h2 className="wpp-title" style={{ fontSize: '1.25rem', color: '#0f172a', fontWeight: '700', margin: '0 0 6px 0' }}>Enlace de WhatsApp</h2>
          <p className="wpp-subtitle" style={{ fontSize: '0.875rem', color: '#64748b', margin: 0, lineHeight: '1.4' }}>
            Vinculá la línea telefónica de tu negocio para activar las respuestas automáticas, cotizaciones y atención inteligente con IA.
          </p>
        </div>

        <div className="wpp-body" style={{ padding: '1.5rem' }}>
          
          {/* BANNER REQUISITO DE CONFIGURACIÓN PREVIA */}
          <div style={{ 
            backgroundColor: '#eff6ff', 
            border: '1px solid #bfdbfe', 
            padding: '14px 16px', 
            borderRadius: '12px', 
            color: '#1e40af', 
            fontSize: '13.5px', 
            marginBottom: '1.5rem', 
            lineHeight: '1.5' 
          }}>
            <div style={{ fontWeight: '700', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '6px' }}>
              💡 Optimizá las respuestas de tu IA
            </div>
            Para que el asistente responda con máxima precisión, asegurate de tener cargados tus <strong>Horarios de atención</strong> y completar los datos comerciales en la sección de <strong>Configuración</strong> (métodos de pago, dirección, permuta mínima y si atendés directo al público o mediante turnos). Si faltan estos datos, la IA usará valores estándar o derivará el chat a un asesor humano.
          </div>
          
          {/* ESTADO: DESCONECTADO */}
          {status === 'DESCONECTADO' && (
            <div className="wpp-state-container" style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <div style={{ background: '#f1f5f9', width: '50px', height: '50px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '1rem' }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="14" height="20" x="5" y="2" rx="2" ry="2"></rect><path d="M12 18h.01"></path></svg>
              </div>
              <div className="wpp-status-badge offline" style={{ backgroundColor: '#fee2e2', color: '#991b1b', border: '1px solid #fee2e2', padding: '4px 12px', borderRadius: '30px', fontWeight: '600', fontSize: '12px', display: 'inline-flex', alignItems: 'center', gap: '6px', marginBottom: '0.75rem' }}>
                <span className="wpp-status-dot" style={{ backgroundColor: '#ef4444', width: '6px', height: '6px', borderRadius: '50%' }}></span>
                Asistente Desconectado
              </div>
              <p className="wpp-instructions" style={{ color: '#475569', fontSize: '14px', maxWidth: '440px', margin: '0 0 1.25rem 0', lineHeight: '1.4' }}>
                Generá un código QR exclusivo y escanealo desde tu aplicación móvil de WhatsApp para iniciar el servicio operativo.
              </p>
              <button 
                onClick={conectarDispositivo} 
                className="btn-wpp-primary"
                style={{ backgroundColor: '#0f172a', color: '#fff', padding: '10px 24px', borderRadius: '8px', fontWeight: '600', border: 'none', cursor: 'pointer', fontSize: '14px', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}
              >
                Generar Código QR
              </button>
            </div>
          )}

          {/* ESTADO: GENERANDO QR */}
          {status === 'GENERANDO' && (
            <div className="wpp-state-container" style={{ textAlign: 'center', padding: '1rem 0' }}>
              <svg className="wpp-spinner primary" viewBox="0 0 50 50" style={{ width: '40px', height: '40px', margin: '0 auto 1rem auto' }}>
                <circle cx="25" cy="25" r="20" fill="none" strokeWidth="4" stroke="#0f172a" style={{ strokeDasharray: '90, 150', strokeDashoffset: '0' }}></circle>
              </svg>
              <h4 style={{ margin: '0 0 6px 0', fontSize: '15px', color: '#0f172a', fontWeight: '600' }}>Asignando canal seguro</h4>
              <p className="wpp-instructions" style={{ color: '#64748b', fontSize: '13px', margin: 0 }}>
                Estableciendo cifrado extremo y configurando webhooks. Aguardá un instante...
              </p>
            </div>
          )}

          {/* ESTADO: MOSTRANDO QR */}
          {status === 'MOSTRANDO_QR' && (
            <div className="wpp-qr-container" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <h3 className="wpp-qr-title" style={{ fontSize: '16px', color: '#0f172a', fontWeight: '600', margin: '0 0 1rem 0' }}>Escaneá el código QR</h3>
              
              <div style={{ alignSelf: 'stretch', textAlign: 'left', background: '#f8fafc', padding: '16px', borderRadius: '12px', border: '1px solid #e2e8f0', marginBottom: '1.5rem', fontSize: '13px', color: '#334155' }}>
                <div style={{ display: 'flex', gap: '10px', marginBottom: '8px' }}>
                  <span style={{ fontWeight: 'bold', color: '#0f172a' }}>1.</span>
                  <span>Abrí <strong>WhatsApp</strong> en tu teléfono celular.</span>
                </div>
                <div style={{ display: 'flex', gap: '10px', marginBottom: '8px' }}>
                  <span style={{ fontWeight: 'bold', color: '#0f172a' }}>2.</span>
                  <span>Tocá en <strong>Menú / Configuración</strong> y seleccioná <strong>Dispositivos vinculados</strong>.</span>
                </div>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <span style={{ fontWeight: 'bold', color: '#0f172a' }}>3.</span>
                  <span>Presioná en <strong>Vincular un dispositivo</strong> y apunta tu cámara hacia la pantalla.</span>
                </div>
              </div>

              {qrCode && (
                <div className="wpp-qr-box" style={{ padding: '16px', background: '#fff', border: '2px solid #cbd5e1', borderRadius: '16px', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.05)', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <img src={qrCode} alt="Código QR de WhatsApp" className="wpp-qr-image" style={{ width: '220px', height: '220px', display: 'block' }} />
                </div>
              )}
              
              <button 
                onClick={() => setStatus('DESCONECTADO')} 
                className="btn-wpp-secondary"
                style={{ background: 'transparent', border: '1px solid #cbd5e1', color: '#64748b', padding: '8px 20px', borderRadius: '8px', fontSize: '13px', fontWeight: '500', cursor: 'pointer' }}
              >
                Cancelar vinculación
              </button>
            </div>
          )}

          {/* ESTADO: CONECTADO */}
          {status === 'CONECTADO' && (
            <div className="wpp-connected-wrapper">
              <div className="wpp-success-box" style={{ background: '#ecfdf5', border: '1px solid #a7f3d0', padding: '1.5rem', borderRadius: '12px', textAlign: 'center', marginBottom: '1.5rem' }}>
                <div style={{ background: '#10b981', width: '44px', height: '44px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1rem auto', color: '#fff', fontSize: '20px' }}>
                  ✓
                </div>
                <div className="wpp-status-badge online" style={{ backgroundColor: '#d1fae5', color: '#065f46', padding: '4px 12px', borderRadius: '20px', fontWeight: '600', fontSize: '12px', display: 'inline-flex', alignItems: 'center', gap: '6px', marginBottom: '0.75rem' }}>
                  <span className="wpp-status-dot" style={{ backgroundColor: '#10b981', width: '6px', height: '6px', borderRadius: '50%' }}></span>
                  Canal de Atención Activo
                </div>
                <p className="wpp-success-text" style={{ margin: 0, fontSize: '14px', color: '#065f46', lineHeight: '1.5', fontWeight: '500' }}>
                  El bot de inteligencia artificial se encuentra conectado correctamente y respondiendo de forma autónoma en la línea comercial.
                </p>
              </div>
              
              <div className="wpp-disconnect-section" style={{ borderTop: '1px solid #f1f5f9', paddingTop: '1.25rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px' }}>
                <div style={{ flex: 1 }}>
                  <h4 style={{ margin: '0 0 2px 0', fontSize: '14px', color: '#0f172a', fontWeight: '600' }}>¿Cambio de línea?</h4>
                  <p className="wpp-disconnect-text" style={{ margin: 0, fontSize: '13px', color: '#64748b', lineHeight: '1.4' }}>Desvinculá el número actual si precisás conectar el celular de otro operador o encargado.</p>
                </div>
                <button 
                  onClick={() => setModalVisible(true)} 
                  className="btn-wpp-danger"
                  style={{ backgroundColor: '#fee2e2', color: '#ef4444', border: 'none', padding: '9px 16px', borderRadius: '8px', fontWeight: '600', fontSize: '13px', cursor: 'pointer', whiteSpace: 'nowrap' }}
                >
                  Desconectar número
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal Moderno de Confirmación para Desconexión */}
      {modalVisible && (
        <div style={{ 
          position: 'fixed', 
          top: 0, left: 0, right: 0, bottom: 0, 
          backgroundColor: 'rgba(15, 23, 42, 0.6)', 
          backdropFilter: 'blur(4px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', 
          zIndex: 99999 
        }}>
          <div style={{ 
            background: '#fff', 
            borderRadius: '16px', 
            padding: '1.75rem', 
            width: '90%', 
            maxWidth: '420px', 
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)' 
          }}>
            <h4 style={{ margin: '0 0 10px 0', fontSize: '18px', color: '#0f172a', fontWeight: '700' }}>
              ¿Desconectar celular del sistema?
            </h4>
            <p style={{ margin: '0 0 24px 0', fontSize: '14px', color: '#475569', lineHeight: '1.5' }}>
              Si confirmás la desvinculación, el agente de Inteligencia Artificial <strong style={{ color: '#ef4444' }}>dejará de responder inmediatamente</strong> a todos tus clientes hasta que asocies un nuevo código QR.
            </p>
            
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
              <button 
                onClick={() => setModalVisible(false)}
                style={{
                  background: 'transparent',
                  border: '1px solid #cbd5e1',
                  padding: '9px 16px',
                  borderRadius: '8px',
                  color: '#475569',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Mantener conectado
              </button>
              <button 
                onClick={ejecutarDesconexion}
                style={{
                  background: '#ef4444',
                  border: 'none',
                  padding: '9px 16px',
                  borderRadius: '8px',
                  color: '#fff',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                }}
              >
                Sí, desactivar bot
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}